import { FC, ReactNode, useEffect, useState } from 'react'
import { Source } from '../../../interfaces/Source'
import { AddSource } from '../../../components/Sources/AddSource'
import { DeleteSource } from '../../../components/Sources/DeleteSource'
import { UpdateSource } from '../../../components/Sources/UpdateSource'
import useUser from '../../../hooks/User'
import { IconButton, Menu, MenuItem } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useIntl } from 'react-intl'
import { CellContext, ColumnDef } from '@tanstack/react-table'
import { MasterDataTableTanstack } from '../../../components/DataTable/MasterDataTableTanstack'
import useDynamicTab from "../../../hooks/useDynamicTab";
import useModalEffect from '../../../hooks/useModalEffect'
import { useSettingsStore } from '../../../stores/SettingsStore'
import { usePipelineStore } from '../../../stores/PipelineStore'

const MasterSources: FC = () => {
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false)
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
    const [source, setSource] = useState<Source>()
    const user = useUser()
    const [openMenuId, setOpenMenuId] = useState<string | null>(null)
    const { settings } = useSettingsStore()
    const intl = useIntl()
    const { multiPipelines } = usePipelineStore()

    const handleClick = (menuId: string) => {
        setOpenMenuId(menuId)
    }
    const handleClose = () => {
        setOpenMenuId(null)
    }

    const columns: ColumnDef<Source>[] = [
        {
            header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ACTION' }),
            meta: {
                size: 1,
                maxSize: 1,
                styleHeader: {
                    alignContent: 'center',
                    textAlign: 'center',
                },
                styleBody: {
                    alignContent: 'center',
                    textAlign: 'center',
                    width: '70px',
                },
            },
            cell: (props: CellContext<Source, unknown>) => {
                const row = props.row.original
                return (
                    <>
                        {(row.creator_id === user.data.id ||
                            user.data.is_superadmin ||
                            user.data.permission_pipeline_source_update !== 'NONE' ||
                            user.data.permission_pipeline_source_delete !== 'NONE') && (
                                <>
                                    <IconButton
                                        aria-label={`more-${row.id}`}
                                        id={`menu-anchor-${row.id}`}
                                        onClick={() => handleClick(row.id)}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>

                                    <Menu
                                        id={`menu-${row.id}`}
                                        anchorEl={document.getElementById(`menu-anchor-${row.id}`)}
                                        open={openMenuId === row.id}
                                        onClose={handleClose}
                                        disableScrollLock={true}
                                    >
                                        {(row.creator_id === user.data.id ||
                                            user.data.is_superadmin ||
                                            user.data.permission_pipeline_source_update !== 'NONE') && (
                                                <MenuItem
                                                    onClick={() => {
                                                        handleClose()
                                                        setShowUpdateModal(true)
                                                        setSource(row)
                                                    }}
                                                >
                                                    <i className='fas fa-edit fs-4 me-4'></i>
                                                    <span>
                                                        {intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}
                                                    </span>
                                                </MenuItem>
                                            )}

                                        {(row.creator_id === user.data.id ||
                                            user.data.is_superadmin ||
                                            user.data.permission_pipeline_source_delete !== 'NONE') && (
                                                <MenuItem
                                                    onClick={() => {
                                                        handleClose()
                                                        setShowDeleteModal(true)
                                                        setSource(row)
                                                    }}
                                                >
                                                    <i className='fas fa-trash fs-3 me-5'></i>
                                                    <span>
                                                        {intl.formatMessage({ id: 'FORM.ACTION.DELETE' })}
                                                    </span>
                                                </MenuItem>
                                            )}
                                    </Menu>
                                </>
                            )}
                    </>
                )
            },
        },
        {
            header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
            accessorKey: 'name',
        },
        {
            id: 'database_pipeline',
            header: intl.formatMessage({ id: 'FORM.LABEL.TEMPLATE' }, { title: settings.pipeline_title }),
            accessorKey: 'listLeadTemplateAccess',
            cell: (props: CellContext<Source, unknown>) => {
                const row = props.row.original
                if (row.listLeadTemplateAccess) {
                    if (row.listLeadTemplateAccess.length === multiPipelines.length) {
                        return <div>
                            {intl.formatMessage({ id: "FILTER.SCOPE.PHRASE.ALL" }, { title: intl.formatMessage({ id: "FORM.LABEL.TEMPLATE" }, { title: settings.pipeline_title }) })}
                        </div>
                    }
                    const filterMultiPipelines = multiPipelines.filter((pipeline: any) => row.listLeadTemplateAccess.includes(pipeline.id))
                    const mergeDatabasePipelineArr = filterMultiPipelines.map((currTemplate: any) => {
                        return currTemplate.name
                    }, '')
                    const mergeDatabasePipeline = mergeDatabasePipelineArr.join(', ')
                    return <div>{mergeDatabasePipeline}</div>
                } else return "-"
            },
            filterFn: (row, _, filterValue) => {
                const templates = row.original.listLeadTemplateAccess;

                // Jika filter kosong, tampilkan semua baris
                if (!Array.isArray(filterValue) || filterValue.length === 0) return true;

                // Kalau templates null atau kosong, baris tidak lolos filter
                if (!templates || templates.length === 0) return false;

                const idTemplates = templates.map((t: any) => t.id);

                return idTemplates.some((val: any) => filterValue.includes(val));
            },

        }
    ]

    let actions: ReactNode[] = []
    if (user.data.permission_pipeline_source_create !== 'NONE') {
        actions.push(<AddSource />)
    }

    useModalEffect({
        showModal: showUpdateModal,
        setShowModal: setShowUpdateModal,
        modalId: `update-source-modal-${source?.id}`
    })

    useModalEffect({
        showModal: showDeleteModal,
        setShowModal: setShowDeleteModal,
        modalId: `delete-source-modal-${source?.id}`
    })

    useDynamicTab({ title: `${intl.formatMessage({ id: 'MENU.SOURCES' })} | CRM WooBlazz` });

    return (
        <>
            <ol className='breadcrumb text-muted fs-6 fw-bold mb-5'>
                <li className='breadcrumb-item text-dark'>
                    {intl.formatMessage({ id: 'MENU.SOURCES' })}
                </li>
            </ol>
            {/* <MasterDataTable
                tableKey="lead_sources"
                tableColumns={tableColumns}
                actions={actions}
                apiURL="lead_sources"
            /> */}
            <MasterDataTableTanstack
                tableKey='lead_sources'
                tableColumns={columns}
                actions={actions}
                apiURL='lead_sources'
                // dateStart={`${date.getFullYear()}-${date.getMonth() + 1}-01`}
                // dateEnd={moment(date).format("YYYY-MM-DD")}
                autoReload={true}
            />
            {showUpdateModal && <UpdateSource source={source!} />}
            {showDeleteModal && <DeleteSource source={source!} />}
        </>
    )
}

export { MasterSources }